import React from 'react';
import Image from 'next/image';
import { motion } from 'framer-motion';

const visionMissionValuesData = [
  {
    id: 'vision',
    title: 'Tầm nhìn',
    description: [
      'Trở thành doanh nghiệp dẫn đầu trong lĩnh vực cung cấp trứng gia cầm tại Việt Nam.',
      'Không ngừng mở rộng quy mô và chuẩn hóa quy trình để đáp ứng nhu cầu ngày càng cao của thị trường.',
      'Xây dựng thương hiệu uy tín, phát triển bền vững và đồng hành cùng sự phát triển của cộng đồng.',
    ],
    image:
      'https://www.eggsunlimited.com/wp-content/uploads/2022/10/brown-eggs-machines.jpg',
    imagePosition: 'left',
  },
  {
    id: 'mission',
    title: 'Sứ mệnh',
    description: [
      'Mang đến nguồn trứng gia cầm chất lượng, an toàn, giàu dinh dưỡng cho mọi gia đình và đối tác.',
      'Cung cấp dịch vụ gia công trứng đạt chuẩn, giúp các doanh nghiệp sản xuất thực phẩm tiết kiệm thời gian và tối ưu vận hành.',
      'Phục vụ khách hàng với tinh thần chuyên nghiệp, lấy uy tín làm trọng tâm trong mọi cam kết.',
    ],
    image:
      'https://media.istockphoto.com/id/1203667122/photo/egg-factory-plant-agriculture-poultry-chicken-farm.jpg?s=612x612&w=0&k=20&c=C3-0T1LTECX4WcFrs5o5ctF6Otyxzv0ehGqxJWU8tSc=',
    imagePosition: 'right',
  },
  {
    id: 'values',
    title: 'Giá trị cốt lõi',
    description: [
      'Uy tín: Luôn giữ lời hứa, minh bạch và trách nhiệm trong mọi hoạt động hợp tác.',
      'Chất lượng: Kiên định với mục tiêu mang đến sản phẩm đạt chuẩn, an toàn và ổn định.',
      'Phát triển bền vững: Hài hòa lợi ích doanh nghiệp, đối tác, người tiêu dùng và môi trường.',
    ],
    image:
      'https://eu-images.contentstack.com/v3/assets/blte5a51c2d28bbcc9c/bltc49dd3c4fd42cab1/68d2a4f61787aabf7e4e11ca/egg_production_inspection_conveyorbelt_-shutterstock.jpg',
    imagePosition: 'left',
  },
];

const VisionMissionValues = () => {
  return (
    <div className="w-full">
      {visionMissionValuesData.map((section, index) => {
        const isImageLeft = section.imagePosition === 'left';
        const isEven = index % 2 === 0;
        const bgClass = isEven ? 'bg-white' : 'bg-primary/5';

        return (
          <div key={section.id} className={`${bgClass} py-8 md:py-16`}>
            <div className="container">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
                {/* Image Section */}
                {isImageLeft ? (
                  <motion.div
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="relative w-full h-[400px] md:h-[500px] lg:h-[600px]"
                  >
                    <div className="relative w-full h-full rounded-3xl overflow-hidden shadow-lg">
                      <Image
                        src={section.image}
                        alt={section.title}
                        fill
                        className="object-cover"
                        sizes="(max-width: 1024px) 100vw, 50vw"
                      />
                    </div>
                  </motion.div>
                ) : null}

                {/* Content Section */}
                <motion.div
                  initial={{ opacity: 0, x: isImageLeft ? 30 : -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="flex flex-col gap-6 md:gap-8"
                >
                  {/* Small Heading */}
                  <motion.p
                    initial={{ opacity: 0, y: -10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="text-primary text-[14px] md:text-[16px] font-semibold uppercase tracking-wider"
                  >
                    {section.title}
                  </motion.p>

                  {/* Main Heading */}
                  <motion.h2
                    initial={{ opacity: 0, y: -10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    className="text-[28px] md:text-[36px] lg:text-[42px] font-bold text-gray2 leading-tight"
                  >
                    {section.title}
                  </motion.h2>

                  {/* Description List */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className="flex flex-col gap-4"
                  >
                    {section.description.map((desc, descIndex) => (
                      <motion.div
                        key={descIndex}
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{
                          duration: 0.4,
                          delay: 0.3 + descIndex * 0.1,
                        }}
                        className="flex items-start gap-3"
                      >
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center mt-1">
                          <svg
                            className="w-4 h-4 text-primary"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={3}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </div>
                        <p className="text-[16px] md:text-[18px] text-gray-600 leading-relaxed">
                          {desc}
                        </p>
                      </motion.div>
                    ))}
                  </motion.div>
                </motion.div>

                {/* Image Section - Right */}
                {!isImageLeft ? (
                  <motion.div
                    initial={{ opacity: 0, x: 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="relative w-full h-[400px] md:h-[500px] lg:h-[600px]"
                  >
                    <div className="relative w-full h-full rounded-3xl overflow-hidden shadow-lg">
                      <Image
                        src={section.image}
                        alt={section.title}
                        fill
                        className="object-cover"
                        sizes="(max-width: 1024px) 100vw, 50vw"
                      />
                    </div>
                  </motion.div>
                ) : null}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default VisionMissionValues;
